import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Plus, Trash2, Pencil, Check, X, ArrowLeft, ExternalLink, GripVertical, WifiOff, Download } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';

const API = process.env.REACT_APP_API_URL;
const fmt = (n) => (n || 0).toLocaleString('ko-KR');

export default function PaperAdminItemsPage() {
  const { id } = useParams();
  const me = useSelector(state => state.auth.me);
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);          // 품목(열)
  const [locations, setLocations] = useState([]);  // 장소(행)
  const [quantities, setQuantities] = useState({}); // {itemId_locationId: qty}
  const [commonItems, setCommonItems] = useState([]); // 공통물품
  const [loading, setLoading] = useState(true);

  // 편집 상태
  const [editingItem, setEditingItem] = useState(null);   // {id, name, spec}
  const [editingLoc, setEditingLoc] = useState(null);     // {id, name}
  const [editingCommon, setEditingCommon] = useState(null); // {id, ...}
  const [newItem, setNewItem] = useState(null);   // {name, spec}
  const [newLoc, setNewLoc] = useState(null);     // {name}
  const [newCommon, setNewCommon] = useState(null); // {name, price, link, buyer, method}

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ─── 실시간 협업 ───
  const COLORS = ['#a78bfa','#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9'];
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [peerCells, setPeerCells] = useState({});
  const versionRef = useRef(0);
  const peersRef = useRef('[]');
  const cellsRef = useRef('{}');

  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const prov = new WebsocketProvider('wss://api.urpink.co.kr/yjs', `event-items-${id}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (provider.wsconnected) setConnected(true);
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    provider.awareness.setLocalStateField('user', {
      name: me?.nickName || me?.name || 'Unknown',
      color: COLORS[(me?.id || 0) % COLORS.length],
      profileImage: me?.profileImage || null,
    });
    const ymap = ydoc.getMap('sync');
    const onMapChange = () => {
      const v = ymap.get('version') || 0;
      if (v > versionRef.current) { versionRef.current = v; fetchData(); }
    };
    ymap.observe(onMapChange);
    const awarenessHandler = () => {
      const myName = me?.nickName || me?.name || 'Unknown';
      const seen = new Set();
      const users = [];
      const cells = {};
      provider.awareness.getStates().forEach((state) => {
        if (!state.user) return;
        if (!seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ name: state.user.name, color: state.user.color, profileImage: state.user.profileImage, isMe: state.user.name === myName });
        }
        if (state.user.editingCell && state.user.name !== myName) {
          cells[state.user.editingCell] = { name: state.user.name, color: state.user.color };
        }
      });
      const idx = users.findIndex(u => u.isMe);
      if (idx > 0) users.unshift(users.splice(idx, 1)[0]);
      const pStr = JSON.stringify(users);
      const cStr = JSON.stringify(cells);
      if (pStr !== peersRef.current) { peersRef.current = pStr; setPeers(users); }
      if (cStr !== cellsRef.current) { cellsRef.current = cStr; setPeerCells(cells); }
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler();
    return () => { provider.destroy(); ydoc.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, ydoc, me]);

  const notifyChange = () => {
    const ymap = ydoc.getMap('sync');
    versionRef.current = (ymap.get('version') || 0) + 1;
    ymap.set('version', versionRef.current);
  };

  const setEditingCell = (cellId) => {
    provider.awareness.setLocalStateField('user', {
      ...provider.awareness.getLocalState()?.user,
      editingCell: cellId,
    });
  };

  const EMPTY_STYLE = useMemo(() => ({}), []);
  const getCellStyle = useCallback((cellId) => {
    const peer = peerCells[cellId];
    if (!peer) return EMPTY_STYLE;
    return { boxShadow: `inset 0 0 0 2px ${peer.color}`, position: 'relative' };
  }, [peerCells, EMPTY_STYLE]);

  const CellCursor = ({ cellId }) => {
    const peer = peerCells[cellId];
    if (!peer) return null;
    return (
      <div className="absolute -top-5 left-0 z-10 pointer-events-none"
        style={{ animation: 'yCursorFadeIn 0.15s ease-out' }}>
        <span className="text-[10px] font-semibold text-white px-1.5 py-0.5 rounded whitespace-nowrap"
          style={{ background: peer.color, borderRadius: '3px 3px 3px 0' }}>{peer.name}</span>
      </div>
    );
  };

  // ─── 드래그 상태 ─────────────────────────────────────────
  const [dragItemId, setDragItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);
  const [dragLocId, setDragLocId] = useState(null);
  const [dragOverLocId, setDragOverLocId] = useState(null);

  const arrayMove = (arr, from, to) => {
    const result = [...arr];
    const [removed] = result.splice(from, 1);
    result.splice(to, 0, removed);
    return result;
  };

  // 품목(열) 드래그
  const onItemDragStart = (e, itemId) => {
    setDragItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onItemDragOver = (e, itemId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (itemId !== dragItemId) setDragOverItemId(itemId);
  };
  const onItemDragEnd = () => { setDragItemId(null); setDragOverItemId(null); };
  const onItemDrop = async (e, targetId) => {
    e.preventDefault();
    if (!dragItemId || dragItemId === targetId) { onItemDragEnd(); return; }
    const oldIdx = items.findIndex(it => it.id === dragItemId);
    const newIdx = items.findIndex(it => it.id === targetId);
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    setDragItemId(null); setDragOverItemId(null);
    try {
      await axios.put(`${API}/api/event-manage/items-table/reorder-items`, next.map((it, i) => ({ id: it.id, order: i })), { headers });
      notifyChange();
    } catch { alert('순서 저장 중 오류가 발생했습니다.'); }
  };

  // 장소(행) 드래그
  const onLocDragStart = (e, locId) => {
    setDragLocId(locId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onLocDragOver = (e, locId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (locId !== dragLocId) setDragOverLocId(locId);
  };
  const onLocDragEnd = () => { setDragLocId(null); setDragOverLocId(null); };
  const onLocDrop = async (e, targetId) => {
    e.preventDefault();
    if (!dragLocId || dragLocId === targetId) { onLocDragEnd(); return; }
    const oldIdx = locations.findIndex(l => l.id === dragLocId);
    const newIdx = locations.findIndex(l => l.id === targetId);
    const next = arrayMove(locations, oldIdx, newIdx);
    setLocations(next);
    setDragLocId(null); setDragOverLocId(null);
    try {
      await axios.put(`${API}/api/event-manage/items-table/reorder-locations`, next.map((l, i) => ({ id: l.id, order: i })), { headers });
      notifyChange();
    } catch { alert('순서 저장 중 오류가 발생했습니다.'); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, tableRes] = await Promise.all([
        axios.get(`${API}/api/event-layout/projects/${id}`, { headers }),
        axios.get(`${API}/api/event-manage/projects/${id}/items-table`, { headers }),
      ]);
      setProject(projRes.data);
      const { items: its, locations: locs, quantities: qtys, commonItems: ci } = tableRes.data;
      setItems(its || []);
      setLocations(locs || []);
      const qMap = {};
      (qtys || []).forEach(q => { qMap[`${q.eventItemId}_${q.eventItemLocationId}`] = q.quantity; });
      setQuantities(qMap);
      setCommonItems(ci || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── 품목(열) CRUD ─────────────────────────────────────

  const addItem = async () => {
    console.log('[addItem] called, newItem:', newItem);
    if (!newItem?.name?.trim()) { console.log('[addItem] empty name, return'); return; }
    try {
      const res = await axios.post(`${API}/api/event-manage/projects/${id}/items-table/items`, newItem, { headers });
      console.log('[addItem] success:', res.data);
      setItems(prev => [...prev, res.data]);
      setNewItem(null);
      notifyChange();
    } catch (e) {
      console.error('[addItem] error:', e?.response?.status, e?.response?.data);
      alert(e?.response?.data?.error || e?.response?.data?.message || '오류가 발생했습니다.');
    }
  };

  const saveItem = async () => {
    if (!editingItem) return;
    try {
      await axios.put(`${API}/api/event-manage/items-table/items/${editingItem.id}`, editingItem, { headers });
      setItems(prev => prev.map(it => it.id === editingItem.id ? { ...it, ...editingItem } : it));
      setEditingItem(null);
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('품목을 삭제하면 모든 수량 데이터도 삭제됩니다. 계속하시겠습니까?')) return;
    try {
      await axios.delete(`${API}/api/event-manage/items-table/items/${itemId}`, { headers });
      setItems(prev => prev.filter(it => it.id !== itemId));
      setQuantities(prev => {
        const next = { ...prev };
        Object.keys(next).filter(k => k.startsWith(`${itemId}_`)).forEach(k => delete next[k]);
        return next;
      });
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // ─── 장소(행) CRUD ─────────────────────────────────────

  const addLocation = async () => {
    if (!newLoc?.name?.trim()) return;
    try {
      const res = await axios.post(`${API}/api/event-manage/projects/${id}/items-table/locations`, newLoc, { headers });
      setLocations(prev => [...prev, res.data]);
      setNewLoc(null);
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const saveLocation = async () => {
    if (!editingLoc) return;
    try {
      await axios.put(`${API}/api/event-manage/items-table/locations/${editingLoc.id}`, editingLoc, { headers });
      setLocations(prev => prev.map(l => l.id === editingLoc.id ? { ...l, ...editingLoc } : l));
      setEditingLoc(null);
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const deleteLocation = async (locId) => {
    if (!window.confirm('장소를 삭제하면 해당 장소의 수량 데이터도 삭제됩니다. 계속하시겠습니까?')) return;
    try {
      await axios.delete(`${API}/api/event-manage/items-table/locations/${locId}`, { headers });
      setLocations(prev => prev.filter(l => l.id !== locId));
      setQuantities(prev => {
        const next = { ...prev };
        Object.keys(next).filter(k => k.endsWith(`_${locId}`)).forEach(k => delete next[k]);
        return next;
      });
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // ─── 수량 ─────────────────────────────────────────────

  const updateQuantity = async (itemId, locationId, value) => {
    const qty = Number(value) || 0;
    const key = `${itemId}_${locationId}`;
    setQuantities(prev => ({ ...prev, [key]: qty }));
    try {
      await axios.put(`${API}/api/event-manage/items-table/quantity`, { eventItemId: itemId, eventItemLocationId: locationId, quantity: qty }, { headers });
      notifyChange();
    } catch { alert('저장 중 오류가 발생했습니다.'); }
  };

  const getQty = (itemId, locationId) => quantities[`${itemId}_${locationId}`] || 0;

  const colTotal = (itemId) => locations.reduce((s, l) => s + getQty(itemId, l.id), 0);

  // ─── 공통물품 CRUD ─────────────────────────────────────

  const addCommon = async () => {
    if (!newCommon?.name?.trim()) return;
    try {
      const res = await axios.post(`${API}/api/event-manage/projects/${id}/items-table/common`, newCommon, { headers });
      setCommonItems(prev => [...prev, res.data]);
      setNewCommon(null);
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const saveCommon = async () => {
    if (!editingCommon) return;
    try {
      await axios.put(`${API}/api/event-manage/items-table/common/${editingCommon.id}`, editingCommon, { headers });
      setCommonItems(prev => prev.map(c => c.id === editingCommon.id ? { ...c, ...editingCommon } : c));
      setEditingCommon(null);
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const deleteCommon = async (commonId) => {
    try {
      await axios.delete(`${API}/api/event-manage/items-table/common/${commonId}`, { headers });
      setCommonItems(prev => prev.filter(c => c.id !== commonId));
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const totalCommonPrice = commonItems.reduce((s, c) => s + ((c.unitPrice || c.price || 0) * (c.quantity || 1)), 0);

  // ─── 엑셀 다운로드 ─────────────────────────────────────

  const BORDER = {
    top:    { style: 'thin', color: { argb: 'FFD4D0E8' } },
    left:   { style: 'thin', color: { argb: 'FFD4D0E8' } },
    bottom: { style: 'thin', color: { argb: 'FFD4D0E8' } },
    right:  { style: 'thin', color: { argb: 'FFD4D0E8' } },
  };
  const BORDER_G = {
    top:    { style: 'thin', color: { argb: 'FFBBE0C8' } },
    left:   { style: 'thin', color: { argb: 'FFBBE0C8' } },
    bottom: { style: 'thin', color: { argb: 'FFBBE0C8' } },
    right:  { style: 'thin', color: { argb: 'FFBBE0C8' } },
  };
  const WHITE = { argb: 'FFFFFFFF' };

  const xlsxTrigger = (buffer, filename) => {
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadMatrixExcel = async () => {
    const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Pink Paper';
    const ws = wb.addWorksheet('장소별 품목 배치');

    const totalCols = items.length + 2;

    // ① 타이틀
    ws.mergeCells(1, 1, 1, totalCols);
    const titleCell = ws.getCell(1, 1);
    titleCell.value = `${project?.title || '행사'} — 장소별 품목 배치`;
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A0E2E' } };
    titleCell.font = { bold: true, size: 14, color: WHITE };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(1).height = 36;

    // ② 헤더
    const headerRow = ws.addRow([
      '장소 / 품목',
      ...items.map(it => it.name + (it.spec ? `\n(${it.spec})` : '')),
      '합계',
    ]);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2D1B69' } };
      cell.font = { bold: true, size: 10, color: WHITE };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = BORDER;
    });
    headerRow.height = items.some(it => it.spec) ? 40 : 24;

    // ③ 데이터 행
    locations.forEach((loc, ri) => {
      const rowTotal = items.reduce((s, it) => s + getQty(it.id, loc.id), 0);
      const row = ws.addRow([loc.name, ...items.map(it => getQty(it.id, loc.id) || 0), rowTotal]);
      const isAlt = ri % 2 === 1;
      row.eachCell((cell, ci) => {
        if (isAlt) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F3FF' } };
        cell.border = BORDER;
        cell.font = { size: 10 };
        if (ci === 1) {
          cell.font = { bold: true, size: 10 };
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.numFmt = '#,##0';
          if (Number(cell.value) === 0) cell.font = { size: 10, color: { argb: 'FFCCCCCC' } };
          else if (ci === totalCols) cell.font = { bold: true, size: 10, color: { argb: 'FF4C1D95' } };
        }
      });
      row.height = 20;
    });

    // ④ 합계 행
    const grandTotal = locations.reduce((s, l) => s + items.reduce((ss, it) => ss + getQty(it.id, l.id), 0), 0);
    const totalRow = ws.addRow(['합계', ...items.map(it => colTotal(it.id)), grandTotal]);
    totalRow.eachCell((cell, ci) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3D1A72' } };
      cell.font = { bold: true, size: 10, color: WHITE };
      cell.alignment = { horizontal: ci === 1 ? 'center' : 'center', vertical: 'middle' };
      cell.border = BORDER;
      if (ci > 1) cell.numFmt = '#,##0';
    });
    totalRow.height = 24;

    // ⑤ 열 너비
    ws.getColumn(1).width = 22;
    items.forEach((_, i) => { ws.getColumn(i + 2).width = Math.max(10, (items[i]?.name?.length || 6) + 2); });
    ws.getColumn(totalCols).width = 12;
    ws.views = [{ state: 'frozen', xSplit: 1, ySplit: 2 }];

    xlsxTrigger(await wb.xlsx.writeBuffer(), `${project?.title || '행사'}_장소별품목배치.xlsx`);
  };

  const downloadCommonExcel = async () => {
    const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Pink Paper';
    const ws = wb.addWorksheet('공통물품');

    // ① 타이틀
    ws.mergeCells(1, 1, 1, 9);
    const titleCell = ws.getCell(1, 1);
    titleCell.value = `${project?.title || '행사'} — 공통물품`;
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF064E3B' } };
    titleCell.font = { bold: true, size: 14, color: WHITE };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(1).height = 36;

    // ② 헤더
    const headerRow = ws.addRow(['품목', '세부내용', '단가', '수량', '금액', '링크', '구매자', '구매일', '구매방법']);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF065F46' } };
      cell.font = { bold: true, size: 10, color: WHITE };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = BORDER_G;
    });
    headerRow.height = 24;

    // ③ 데이터 행
    commonItems.forEach((c, ri) => {
      const price = (c.unitPrice || c.price || 0) * (c.quantity || 1);
      const row = ws.addRow([
        c.name, c.description || '',
        c.unitPrice || c.price || 0,
        c.quantity || 1,
        price,
        c.link || '',
        c.buyer || '',
        c.purchaseDate || '',
        c.method || '',
      ]);
      if (ri % 2 === 1) row.eachCell(cell => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0FDF4' } }; });
      row.eachCell((cell, ci) => {
        cell.border = BORDER_G;
        cell.font = { size: 10 };
        if (ci <= 2) cell.alignment = { horizontal: 'left', vertical: 'middle' };
        else if (ci === 6) cell.alignment = { horizontal: 'left', vertical: 'middle' };
        else if ([3, 4, 5].includes(ci)) { cell.alignment = { horizontal: 'right', vertical: 'middle' }; cell.numFmt = '#,##0'; }
        else cell.alignment = { horizontal: 'center', vertical: 'middle' };
        if (ci === 5 && Number(cell.value) > 0) cell.font = { bold: true, size: 10, color: { argb: 'FF047857' } };
      });
      row.height = 20;
    });

    // ④ 합계 행
    ws.mergeCells(commonItems.length + 3, 1, commonItems.length + 3, 4);
    const totalRow = ws.addRow(['합계', '', '', '', totalCommonPrice, '', '', '', '']);
    totalRow.eachCell((cell, ci) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF047857' } };
      cell.font = { bold: true, size: 10, color: WHITE };
      cell.border = BORDER_G;
      if (ci === 5) { cell.numFmt = '#,##0'; cell.alignment = { horizontal: 'right', vertical: 'middle' }; }
      else cell.alignment = { horizontal: ci === 1 ? 'right' : 'center', vertical: 'middle' };
    });
    totalRow.height = 24;

    // ⑤ 열 너비
    ws.getColumn(1).width = 20;
    ws.getColumn(2).width = 24;
    ws.getColumn(3).width = 14;
    ws.getColumn(4).width = 8;
    ws.getColumn(5).width = 14;
    ws.getColumn(6).width = 28;
    ws.getColumn(7).width = 12;
    ws.getColumn(8).width = 14;
    ws.getColumn(9).width = 14;
    ws.views = [{ state: 'frozen', ySplit: 2 }];

    xlsxTrigger(await wb.xlsx.writeBuffer(), `${project?.title || '행사'}_공통물품.xlsx`);
  };

  const cellCls = "border border-white/10 px-2 py-1.5 text-xs";
  const inputCls = "w-full bg-transparent text-white text-xs outline-none focus:bg-white/5 rounded px-1 py-0.5";
  const numInputCls = "w-full bg-transparent text-white text-xs outline-none focus:bg-white/5 rounded px-1 py-0.5 text-center";

  if (loading) return <div className="bg-[#0d0b14] min-h-screen flex items-center justify-center text-white/40">로딩 중...</div>;

  return (
    <div className="bg-[#0d0b14] min-h-screen pb-20">
      {/* 헤더 */}
      <section className="pt-10 pb-6">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-1">
            <Link to={`${PAPER_BASE}/layouts`} className="text-white/40 hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <h1 className="text-white text-2xl font-bold">
              {project?.title || ''} 렌탈관리
            </h1>
            <div className="flex items-center gap-2 ml-auto">
              <div className="flex -space-x-1.5">
                {peers.slice(0, 6).map((p, i) => (
                  <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 overflow-hidden ${p.isMe ? 'border-violet-400' : 'border-[#0d0b14]'}`}
                    style={{ backgroundColor: p.profileImage ? 'transparent' : p.color }} title={p.isMe ? `${p.name} (나)` : p.name}>
                    {p.profileImage ? <img src={p.profileImage} alt="" className="w-full h-full object-cover" /> : p.name?.charAt(0)}
                  </div>
                ))}
              </div>
              {!connected && <WifiOff size={14} className="text-red-400" title="오프라인" />}
            </div>
          </div>
          <p className="text-white/40 text-sm mt-3 ml-7">현장 배치 품목과 장소별 수량을 관리합니다</p>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 space-y-6">

        {/* ─── 품목 배치 매트릭스 ─── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-semibold text-base">장소별 품목 배치</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadMatrixExcel}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/60 hover:text-white text-xs rounded-lg transition-colors border border-white/10">
                <Download size={13} /> 엑셀
              </button>
              <button
                onClick={() => setNewItem({ name: '', spec: '' })}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-500/15 hover:bg-sky-500/25 text-sky-400 text-xs rounded-lg transition-colors">
                <Plus size={13} /> 품목 추가
              </button>
              <button
                onClick={() => setNewLoc({ name: '' })}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-500/15 hover:bg-violet-500/25 text-violet-400 text-xs rounded-lg transition-colors">
                <Plus size={13} /> 장소 추가
              </button>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <div
              className="overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-white/5 [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-white/35"
              onDragOver={e => (dragItemId || dragLocId) && e.preventDefault()}>
              <table className="border-collapse">
                <thead>
                  <tr className="bg-white/5">
                    {/* 장소 헤더 열 */}
                    <th className={`${cellCls} text-white/40 text-left sticky left-0 bg-[#131120] z-10 min-w-[120px]`}>장소 \ 품목</th>
                    {/* 품목 헤더 */}
                    {items.map(item => (
                      <th key={item.id}
                        draggable
                        onDragStart={e => onItemDragStart(e, item.id)}
                        onDragOver={e => onItemDragOver(e, item.id)}
                        onDrop={e => onItemDrop(e, item.id)}
                        onDragEnd={onItemDragEnd}
                        className={`${cellCls} text-center min-w-[64px] max-w-[80px] w-16 transition-colors
                          ${dragItemId === item.id ? 'opacity-40' : ''}
                          ${dragOverItemId === item.id ? 'bg-sky-500/20' : ''}`}>
                        {editingItem?.id === item.id ? (
                          <div className="flex flex-col gap-1">
                            <input value={editingItem.name} onChange={e => setEditingItem({ ...editingItem, name: e.target.value })}
                              className={inputCls} placeholder="품목명" autoFocus />
                            <input value={editingItem.spec || ''} onChange={e => setEditingItem({ ...editingItem, spec: e.target.value })}
                              className={inputCls} placeholder="규격(선택)" />
                            <div className="flex gap-1 justify-center">
                              <button onClick={saveItem} className="text-emerald-400 hover:text-emerald-300"><Check size={12} /></button>
                              <button onClick={() => setEditingItem(null)} className="text-white/40 hover:text-white"><X size={12} /></button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-0.5">
                            <GripVertical size={10} className="text-white/20 cursor-grab mb-0.5" />
                            <span className="text-white font-medium text-[11px] truncate max-w-[72px] block">{item.name}</span>
                            {item.spec && <span className="text-white/40 text-[10px] truncate max-w-[72px] block">{item.spec}</span>}
                            <div className="flex gap-1 mt-0.5">
                              <button onClick={() => setEditingItem({ id: item.id, name: item.name, spec: item.spec || '' })}
                                className="text-white/20 hover:text-sky-400 transition-colors"><Pencil size={11} /></button>
                              <button onClick={() => deleteItem(item.id)}
                                className="text-white/20 hover:text-red-400 transition-colors"><Trash2 size={11} /></button>
                            </div>
                          </div>
                        )}
                      </th>
                    ))}
                    {/* 새 품목 추가 헤더 */}
                    {newItem !== null && (
                      <th className={`${cellCls} min-w-[80px] w-20`}>
                        <div className="flex flex-col gap-1">
                          <input value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })}
                            className={inputCls} placeholder="품목명" autoFocus
                            onKeyDown={e => e.key === 'Enter' && addItem()} />
                          <input value={newItem.spec || ''} onChange={e => setNewItem({ ...newItem, spec: e.target.value })}
                            className={inputCls} placeholder="규격(선택)" />
                          <div className="flex gap-1 justify-center">
                            <button onClick={addItem} className="text-emerald-400 hover:text-emerald-300"><Check size={12} /></button>
                            <button onClick={() => setNewItem(null)} className="text-white/40 hover:text-white"><X size={12} /></button>
                          </div>
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {/* 장소 행 */}
                  {locations.map(loc => (
                    <tr key={loc.id}
                      draggable
                      onDragStart={e => onLocDragStart(e, loc.id)}
                      onDragOver={e => onLocDragOver(e, loc.id)}
                      onDrop={e => onLocDrop(e, loc.id)}
                      onDragEnd={onLocDragEnd}
                      className={`transition-colors
                        ${dragLocId === loc.id ? 'opacity-40' : 'hover:bg-white/3'}
                        ${dragOverLocId === loc.id ? 'bg-violet-500/15' : ''}`}>
                      <td className={`${cellCls} sticky left-0 z-10 ${dragOverLocId === loc.id ? 'bg-violet-900/40' : 'bg-[#131120]'}`}>
                        {editingLoc?.id === loc.id ? (
                          <div className="flex items-center gap-1">
                            <input value={editingLoc.name} onChange={e => setEditingLoc({ ...editingLoc, name: e.target.value })}
                              className={inputCls} autoFocus onKeyDown={e => e.key === 'Enter' && saveLocation()} />
                            <button onClick={saveLocation} className="text-emerald-400 hover:text-emerald-300 flex-shrink-0"><Check size={12} /></button>
                            <button onClick={() => setEditingLoc(null)} className="text-white/40 hover:text-white flex-shrink-0"><X size={12} /></button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <GripVertical size={12} className="text-white/20 cursor-grab flex-shrink-0" />
                            <span className="text-white flex-1">{loc.name}</span>
                            <button onClick={() => setEditingLoc({ id: loc.id, name: loc.name })}
                              className="text-white/20 hover:text-violet-400 transition-colors flex-shrink-0"><Pencil size={11} /></button>
                            <button onClick={() => deleteLocation(loc.id)}
                              className="text-white/20 hover:text-red-400 transition-colors flex-shrink-0"><Trash2 size={11} /></button>
                          </div>
                        )}
                      </td>
                      {items.map(item => {
                        const cellId = `qty-${item.id}-${loc.id}`;
                        return (
                          <td key={item.id} className={`${cellCls} text-center relative`} style={getCellStyle(cellId)}>
                            <CellCursor cellId={cellId} />
                            <input
                              type="number"
                              min="0"
                              value={getQty(item.id, loc.id) || ''}
                              onChange={e => updateQuantity(item.id, loc.id, e.target.value)}
                              onFocus={() => setEditingCell(cellId)}
                              onBlur={() => setEditingCell(null)}
                              placeholder="0"
                              className={`${numInputCls} w-full`}
                            />
                          </td>
                        );
                      })}
                      {newItem !== null && <td className={cellCls}></td>}
                    </tr>
                  ))}

                  {/* 새 장소 추가 행 */}
                  {newLoc !== null && (
                    <tr className="bg-white/3">
                      <td className={`${cellCls} sticky left-0 bg-[#1a1428] z-10`}>
                        <div className="flex items-center gap-1">
                          <input value={newLoc.name} onChange={e => setNewLoc({ ...newLoc, name: e.target.value })}
                            className={inputCls} placeholder="장소명" autoFocus
                            onKeyDown={e => e.key === 'Enter' && addLocation()} />
                          <button onClick={addLocation} className="text-emerald-400 hover:text-emerald-300 flex-shrink-0"><Check size={12} /></button>
                          <button onClick={() => setNewLoc(null)} className="text-white/40 hover:text-white flex-shrink-0"><X size={12} /></button>
                        </div>
                      </td>
                      {items.map(item => <td key={item.id} className={cellCls}></td>)}
                      {newItem !== null && <td className={cellCls}></td>}
                    </tr>
                  )}
                </tbody>

                {/* 합계 행 */}
                {locations.length > 0 && items.length > 0 && (
                  <tfoot>
                    <tr className="bg-white/5">
                      <td className={`${cellCls} text-white/40 font-medium sticky left-0 bg-[#1a1428] z-10`}>합계</td>
                      {items.map(item => (
                        <td key={item.id} className={`${cellCls} text-center font-semibold text-sky-400`}>
                          {colTotal(item.id) || '-'}
                        </td>
                      ))}
                      {newItem !== null && <td className={cellCls}></td>}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {items.length === 0 && locations.length === 0 && (
              <div className="text-center text-white/30 py-12">
                <p className="mb-1">품목과 장소를 추가해 배치 현황을 관리하세요</p>
                <p className="text-xs">우측 상단 버튼으로 품목(열)과 장소(행)를 추가할 수 있습니다</p>
              </div>
            )}
          </div>
        </div>

        {/* ─── 공통물품 ─── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-white font-semibold text-base">공통물품</h2>
              <p className="text-white/40 text-xs mt-0.5">특정 장소에 배치되지 않고 공통으로 사용하는 물품</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadCommonExcel}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/60 hover:text-white text-xs rounded-lg transition-colors border border-white/10">
                <Download size={13} /> 엑셀
              </button>
              <button
                onClick={() => setNewCommon({ name: '', description: '', unitPrice: 0, quantity: 1, price: 0, link: '', buyer: '', purchaseDate: '', method: '' })}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 text-xs rounded-lg transition-colors">
                <Plus size={13} /> 항목 추가
              </button>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-white/5 text-white/40">
                  <th className={`${cellCls} text-center w-32`}>품목</th>
                  <th className={`${cellCls} text-center w-40`}>세부내용</th>
                  <th className={`${cellCls} text-center w-24`}>단가</th>
                  <th className={`${cellCls} text-center w-16`}>수량</th>
                  <th className={`${cellCls} text-center w-24`}>금액</th>
                  <th className={`${cellCls} text-center w-28`}>링크</th>
                  <th className={`${cellCls} text-center w-24`}>구매자</th>
                  <th className={`${cellCls} text-center w-28`}>구매일</th>
                  <th className={`${cellCls} text-center w-24`}>구매방법</th>
                  <th className={`${cellCls} w-8`}></th>
                </tr>
              </thead>
              <tbody>
                {commonItems.map(c => (
                  <tr key={c.id} className="hover:bg-white/3 transition-colors">
                    {editingCommon?.id === c.id ? (
                      <>
                        <td className={`${cellCls} relative`} style={getCellStyle(`common-name-${c.id}`)}><CellCursor cellId={`common-name-${c.id}`} /><input value={editingCommon.name} onChange={e => setEditingCommon({ ...editingCommon, name: e.target.value })} onFocus={() => setEditingCell(`common-name-${c.id}`)} onBlur={() => setEditingCell(null)} className={inputCls} autoFocus /></td>
                        <td className={cellCls}><input value={editingCommon.description || ''} onChange={e => setEditingCommon({ ...editingCommon, description: e.target.value })} className={inputCls} placeholder="세부내용" /></td>
                        <td className={`${cellCls} relative`} style={getCellStyle(`common-price-${c.id}`)}><CellCursor cellId={`common-price-${c.id}`} /><input type="number" value={editingCommon.unitPrice || 0} onChange={e => { const up = Number(e.target.value) || 0; setEditingCommon({ ...editingCommon, unitPrice: up, price: up * (editingCommon.quantity || 1) }); }} onFocus={() => setEditingCell(`common-price-${c.id}`)} onBlur={() => setEditingCell(null)} className={`${numInputCls} text-right`} /></td>
                        <td className={`${cellCls} relative`} style={getCellStyle(`common-qty-${c.id}`)}><CellCursor cellId={`common-qty-${c.id}`} /><input type="number" value={editingCommon.quantity || 1} min="1" onChange={e => { const q = Number(e.target.value) || 1; setEditingCommon({ ...editingCommon, quantity: q, price: (editingCommon.unitPrice || 0) * q }); }} onFocus={() => setEditingCell(`common-qty-${c.id}`)} onBlur={() => setEditingCell(null)} className={`${numInputCls} text-center`} /></td>
                        <td className={`${cellCls} text-right text-white/40`}>{fmt((editingCommon.unitPrice || 0) * (editingCommon.quantity || 1))}원</td>
                        <td className={cellCls}><input value={editingCommon.link || ''} onChange={e => setEditingCommon({ ...editingCommon, link: e.target.value })} className={inputCls} placeholder="https://..." /></td>
                        <td className={cellCls}>
                          <select value={editingCommon.buyer || ''} onChange={e => setEditingCommon({ ...editingCommon, buyer: e.target.value })} className={`${inputCls} cursor-pointer`}>
                            <option value="">선택</option>
                            <option value="김태연">김태연</option>
                            <option value="이정찬">이정찬</option>
                            <option value="김상경">김상경</option>
                          </select>
                        </td>
                        <td className={cellCls}><input type="date" value={editingCommon.purchaseDate || ''} onChange={e => setEditingCommon({ ...editingCommon, purchaseDate: e.target.value })} className={`${inputCls} cursor-pointer`} /></td>
                        <td className={cellCls}>
                          <select value={editingCommon.method || ''} onChange={e => setEditingCommon({ ...editingCommon, method: e.target.value })} className={`${inputCls} cursor-pointer`}>
                            <option value="">선택</option>
                            <option value="법인카드">법인카드</option>
                            <option value="법인이체">법인이체</option>
                            <option value="개인카드">개인카드</option>
                            <option value="개인이체">개인이체</option>
                            <option value="협력사">협력사</option>
                          </select>
                        </td>
                        <td className={`${cellCls} text-center`}>
                          <div className="flex items-center gap-1 justify-center">
                            <button onClick={saveCommon} className="text-emerald-400 hover:text-emerald-300"><Check size={13} /></button>
                            <button onClick={() => setEditingCommon(null)} className="text-white/40 hover:text-white"><X size={13} /></button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className={`${cellCls} text-white relative`} style={getCellStyle(`common-name-${c.id}`)}><CellCursor cellId={`common-name-${c.id}`} />{c.name}</td>
                        <td className={`${cellCls} text-white/50`}>{c.description || '-'}</td>
                        <td className={`${cellCls} text-right text-white/60 relative`} style={getCellStyle(`common-price-${c.id}`)}><CellCursor cellId={`common-price-${c.id}`} />{fmt(c.unitPrice || c.price)}원</td>
                        <td className={`${cellCls} text-center text-white/60 relative`} style={getCellStyle(`common-qty-${c.id}`)}><CellCursor cellId={`common-qty-${c.id}`} />{c.quantity || 1}</td>
                        <td className={`${cellCls} text-right text-white`}>{fmt((c.unitPrice || c.price || 0) * (c.quantity || 1))}원</td>
                        <td className={`${cellCls} text-center`}>
                          {c.link ? (
                            <a href={c.link} target="_blank" rel="noopener noreferrer"
                              className="flex items-center justify-center gap-1 text-sky-400 hover:text-sky-300 transition-colors">
                              <ExternalLink size={12} /> 링크
                            </a>
                          ) : <span className="text-white/20">-</span>}
                        </td>
                        <td className={`${cellCls} text-center text-white/70`}>{c.buyer || '-'}</td>
                        <td className={`${cellCls} text-center text-white/70`}>{c.purchaseDate || '-'}</td>
                        <td className={`${cellCls} text-center text-white/70`}>{c.method || '-'}</td>
                        <td className={`${cellCls} text-center`}>
                          <div className="flex items-center gap-1 justify-center">
                            <button onClick={() => setEditingCommon({ id: c.id, name: c.name, description: c.description || '', unitPrice: c.unitPrice || c.price || 0, quantity: c.quantity || 1, price: c.price || 0, link: c.link || '', buyer: c.buyer || '', purchaseDate: c.purchaseDate || '', method: c.method || '' })}
                              className="text-white/20 hover:text-emerald-400 transition-colors"><Pencil size={13} /></button>
                            <button onClick={() => deleteCommon(c.id)}
                              className="text-white/20 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}

                {/* 새 공통물품 입력 행 */}
                {newCommon !== null && (
                  <tr className="bg-white/3">
                    <td className={cellCls}><input value={newCommon.name} onChange={e => setNewCommon({ ...newCommon, name: e.target.value })} className={inputCls} placeholder="품목명" autoFocus onKeyDown={e => e.key === 'Enter' && addCommon()} /></td>
                    <td className={cellCls}><input value={newCommon.description || ''} onChange={e => setNewCommon({ ...newCommon, description: e.target.value })} className={inputCls} placeholder="세부내용" /></td>
                    <td className={cellCls}><input type="number" value={newCommon.unitPrice || 0} onChange={e => { const up = Number(e.target.value) || 0; setNewCommon({ ...newCommon, unitPrice: up, price: up * (newCommon.quantity || 1) }); }} className={`${numInputCls} text-right`} placeholder="단가" /></td>
                    <td className={cellCls}><input type="number" value={newCommon.quantity || 1} min="1" onChange={e => { const q = Number(e.target.value) || 1; setNewCommon({ ...newCommon, quantity: q, price: (newCommon.unitPrice || 0) * q }); }} className={`${numInputCls} text-center`} placeholder="수량" /></td>
                    <td className={`${cellCls} text-right text-white/40`}>{fmt((newCommon.unitPrice || 0) * (newCommon.quantity || 1))}원</td>
                    <td className={cellCls}><input value={newCommon.link || ''} onChange={e => setNewCommon({ ...newCommon, link: e.target.value })} className={inputCls} placeholder="https://..." /></td>
                    <td className={cellCls}>
                      <select value={newCommon.buyer || ''} onChange={e => setNewCommon({ ...newCommon, buyer: e.target.value })} className={`${inputCls} cursor-pointer`}>
                        <option value="">선택</option>
                        <option value="김태연">김태연</option>
                        <option value="이정찬">이정찬</option>
                        <option value="김상경">김상경</option>
                      </select>
                    </td>
                    <td className={cellCls}><input type="date" value={newCommon.purchaseDate || ''} onChange={e => setNewCommon({ ...newCommon, purchaseDate: e.target.value })} className={`${inputCls} cursor-pointer`} /></td>
                    <td className={cellCls}>
                      <select value={newCommon.method || ''} onChange={e => setNewCommon({ ...newCommon, method: e.target.value })} className={`${inputCls} cursor-pointer`}>
                        <option value="">선택</option>
                        <option value="법인카드">법인카드</option>
                        <option value="법인이체">법인이체</option>
                        <option value="개인카드">개인카드</option>
                        <option value="개인이체">개인이체</option>
                        <option value="협력사">협력사</option>
                      </select>
                    </td>
                    <td className={`${cellCls} text-center`}>
                      <div className="flex items-center gap-1 justify-center">
                        <button onClick={addCommon} className="text-emerald-400 hover:text-emerald-300"><Check size={13} /></button>
                        <button onClick={() => setNewCommon(null)} className="text-white/40 hover:text-white"><X size={13} /></button>
                      </div>
                    </td>
                  </tr>
                )}

                {commonItems.length === 0 && newCommon === null && (
                  <tr>
                    <td colSpan={10} className="text-center text-white/30 py-8">
                      공통물품이 없습니다. "항목 추가"를 눌러 추가하세요.
                    </td>
                  </tr>
                )}
              </tbody>

              {commonItems.length > 0 && (
                <tfoot>
                  <tr className="bg-white/5">
                    <td className={`${cellCls} text-right text-white/40 font-medium`} colSpan={4}>합계</td>
                    <td className={`${cellCls} text-right text-emerald-400 font-semibold`}>{fmt(totalCommonPrice)}원</td>
                    <td colSpan={5} className={cellCls}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
